import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarChart, FileText, Filter, Download, Printer } from 'lucide-react';
import {
    getAllDailyCollections,
    getDailyRecoveryCollections,
    getDailyFlowCollections
} from '../actions/dailyMisActions';
import { getUsers } from '../actions/userActions';
import { getProcesses } from '../actions/processActions';
import {
    GET_ALL_DAILY_COLLECTIONS_RESET,
    GET_DAILY_RECOVERY_COLLECTIONS_RESET,
    GET_DAILY_FLOW_COLLECTIONS_RESET
} from '../constants/dailyMisConstants';

const DailyMIS = () => {
    const dispatch = useDispatch();

    // Redux state selectors
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const getUsersInfo = useSelector((state) => state.getUsersInfo);
    const { getUsersData } = getUsersInfo;

    const getProcessesInfo = useSelector((state) => state.getProcessesInfo);
    const { getProcessesData } = getProcessesInfo;

    const allDailyCollectionsInfo = useSelector((state) => state.allDailyCollectionsInfo);
    const { loadingAllDailyCollections, errorAllDailyCollections, allDailyCollectionsData } = allDailyCollectionsInfo;

    const dailyRecoveryCollectionsInfo = useSelector((state) => state.dailyRecoveryCollectionsInfo);
    const { loadingDailyRecoveryCollections, errorDailyRecoveryCollections, dailyRecoveryCollectionsData } = dailyRecoveryCollectionsInfo;

    const dailyFlowCollectionsInfo = useSelector((state) => state.dailyFlowCollectionsInfo);
    const { loadingDailyFlowCollections, errorDailyFlowCollections, dailyFlowCollectionsData } = dailyFlowCollectionsInfo;

    // Local state
    const [page, setPage] = useState(1);
    const limit = 10;
    const [filters, setFilters] = useState({
        supervisor: '',
        process: '',
        startDate: '',
        endDate: '',
        processType: ''
    });
    const [activeTab, setActiveTab] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [stats, setStats] = useState({
        totalCases: 0,
        totalPos: 0,
        totalRes: 0,
        totalResCase: 0,
        totalCollection: 0,
        totalCurrentProvisionalBilling: 0,
        finalProvisionalBilling: 0,
        count: 0
    });



    const printTable = () => {
        // Get the current data for printing
        let printData = displayCollections;

        // Create print content
        const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Daily MIS Report - ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</title>
            <style>
                body { 
                    font-family: Arial, sans-serif; 
                    margin: 20px;
                    font-size: 12px;
                }
                .header {
                    text-align: center;
                    margin-bottom: 20px;
                    border-bottom: 2px solid #333;
                    padding-bottom: 10px;
                }
                .stats {
                    margin-bottom: 20px;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                }
                .stat-box {
                    border: 1px solid #ddd;
                    padding: 10px;
                    text-align: center;
                    background-color: #f9f9f9;
                }
                .stat-label {
                    font-size: 10px;
                    color: #666;
                    margin-bottom: 5px;
                }
                .stat-value {
                    font-size: 14px;
                    font-weight: bold;
                    color: #333;
                }
                table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    margin-top: 10px;
                }
                th, td { 
                    border: 1px solid #ddd; 
                    padding: 8px; 
                    text-align: left;
                    font-size: 10px;
                }
                th { 
                    background-color: #f2f2f2; 
                    font-weight: bold;
                }
                .currency {
                    text-align: right;
                    font-weight: bold;
                }
                .process-type {
                    background-color: #e3f2fd;
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-size: 9px;
                }
                .footer {
                    margin-top: 20px;
                    text-align: center;
                    font-size: 10px;
                    color: #666;
                }
                @media print {
                    body { margin: 0; }
                    .no-print { display: none; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>Daily MIS Report</h1>
                <h2>${activeTab === 'all' ? 'All Collections' :
                activeTab === 'flow' ? 'Flow Process Collections' : 'Recovery Process Collections'}</h2>
                <p>Generated on: ${new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })}</p>
                ${filters.startDate || filters.endDate ?
                `<p>Date Range: ${filters.startDate || 'Start'} to ${filters.endDate || 'End'}</p>` :
                '<p>All Dates</p>'
            }
            </div>

            <div class="stats">
                <div class="stat-box">
                    <div class="stat-label">Total Cases</div>
                    <div class="stat-value">${(stats.totalCases || 0).toLocaleString()}</div>
                </div>
                <div class="stat-box">
                    <div class="stat-label">Total POS</div>
                    <div class="stat-value">${(stats.totalPos || 0).toLocaleString()}</div>
                </div>
                <div class="stat-box">
                    <div class="stat-label">Total RES%</div>
                    <div class="stat-value">${(stats.totalRes || 0).toLocaleString()}</div>
                </div>
                <div class="stat-box">
                    <div class="stat-label">Total RES Case</div>
                    <div class="stat-value">${(stats.totalResCase || 0).toLocaleString()}</div>
                </div>
                <div class="stat-box">
                    <div class="stat-label">Total Collection</div>
                    <div class="stat-value">₹${(stats.totalCollection || 0).toLocaleString()}</div>
                </div>
                <div class="stat-box">
                    <div class="stat-label">NORM</div>
                    <div class="stat-value">${(stats.totalNorm || 0).toLocaleString()}</div>
                </div>
                <div class="stat-box">
                    <div class="stat-label">RB %</div>
                    <div class="stat-value">${(stats.totalRbPercentage || 0).toLocaleString()}%</div>
                </div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Supervisor</th>
                        <th>Process</th>
                        <th>Type</th>
                        <th>Case</th>
                        <th>POS</th>
                        <th>RES%</th>
                        <th>RES Case</th>
                        <th>NORM</th>
                        <th>RB %</th>
                        <th>Total Collection</th>
                    </tr>
                </thead>
                <tbody>
                    ${printData.map(collection => `
                        <tr>
                            <td>${formatDate(collection.date)}</td>
                            <td>${collection.supervisor?.name || 'Unknown'}</td>
                            <td>${collection.process?.process || 'Unknown'}</td>
                            <td>
                                <span class="process-type">
                                    ${collection.process?.processType || 'Unknown'}
                                </span>
                            </td>
                            <td>${collection.case || 0}</td>
                            <td>${collection.pos || 0}</td>
                            <td>${collection.res || 0}</td>
                            <td>${collection.rescase || 0}</td>
                            <td>${collection.norm || 0}</td>
                            <td>${collection.rbPercentage || 0}%</td>
                            <td class="currency">₹${(collection.totalCollection || 0).toLocaleString()}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>

            <div class="footer">
                <p>Total Records: ${printData.length}</p>
                <p>Report generated from Daily MIS System</p>
            </div>
        </body>
        </html>
    `;

        // Open print window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();

        // Wait for content to load then print
        printWindow.onload = function () {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };


    // Get data from Redux state based on active tab
    const getCurrentCollectionData = () => {
        switch (activeTab) {
            case 'flow':
                return dailyFlowCollectionsData;
            case 'recovery':
                return dailyRecoveryCollectionsData;
            default:
                return allDailyCollectionsData;
        }
    };

    const getCurrentLoadingState = () => {
        switch (activeTab) {
            case 'flow':
                return loadingDailyFlowCollections;
            case 'recovery':
                return loadingDailyRecoveryCollections;
            default:
                return loadingAllDailyCollections;
        }
    };

    const getCurrentErrorState = () => {
        switch (activeTab) {
            case 'flow':
                return errorDailyFlowCollections;
            case 'recovery':
                return errorDailyRecoveryCollections;
            default:
                return errorAllDailyCollections;
        }
    };

    const currentCollectionData = getCurrentCollectionData();
    const collections = currentCollectionData?.data || [];
    const totalPages = currentCollectionData?.totalPages || 1;
    const loading = getCurrentLoadingState();
    const error = getCurrentErrorState();

    // Get supervisors and processes from Redux state
    const supervisors = getUsersData?.users?.filter((u) => u.role?.name === 'Supervisor') || [];
    const allProcesses = Array.isArray(getProcessesData) ? getProcessesData : (getProcessesData?.data || []);

    // Filter processes based on active tab
    const getFilteredProcesses = () => {
        if (activeTab === 'all') {
            return allProcesses;
        }
        const processType = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
        return allProcesses.filter(p => p.processType === processType);
    };

    const processes = getFilteredProcesses();

    // Fetch initial data
    useEffect(() => {
        if (userInfo) {
            dispatch(getUsers());
            dispatch(getProcesses());
        }
    }, [dispatch, userInfo]);

    // Fetch collections when dependencies change
    useEffect(() => {
        if (userInfo) {
            fetchCollections();
        }
    }, [dispatch, userInfo, page, filters, activeTab]);

    // Calculate stats whenever collections data changes
    useEffect(() => {
        calculateStats();
    }, [collections, activeTab]);

    const fetchCollections = () => {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });

        if (filters.supervisor) params.append('supervisor', filters.supervisor);
        if (filters.process) params.append('process', filters.process);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        // Add processType parameter based on tab
        if (activeTab === 'flow') {
            params.append('processType', 'Flow');
        } else if (activeTab === 'recovery') {
            params.append('processType', 'Recovery');
        }

        // Dispatch appropriate action based on active tab
        switch (activeTab) {
            case 'flow':
                dispatch(getDailyFlowCollections(params.toString()));
                break;
            case 'recovery':
                dispatch(getDailyRecoveryCollections(params.toString()));
                break;
            default:
                dispatch(getAllDailyCollections(params.toString()));
                break;
        }
    };

    // Calculate aggregated statistics from collections data
    const calculateStats = () => {
        if (!collections || collections.length === 0) {
            setStats({
                totalCases: 0,
                totalPos: 0,
                totalRes: 0,
                totalResCase: 0,
                totalCollection: 0,
                totalCurrentProvisionalBilling: 0,
                finalProvisionalBilling: 0,
                count: 0
            });
            return;
        }

        // Additional client-side filtering to ensure only correct process type data is shown
        let filteredData = collections;
        if (activeTab === 'flow') {
            filteredData = collections.filter(item => item.process?.processType === 'Flow');
        } else if (activeTab === 'recovery') {
            filteredData = collections.filter(item => item.process?.processType === 'Recovery');
        }

        const aggregatedStats = filteredData.reduce((acc, collection) => {
            return {
                totalCases: acc.totalCases + (collection.case || 0),
                totalPos: acc.totalPos + (collection.pos || 0),
                totalRes: acc.totalRes + (collection.res || 0),
                totalResCase: acc.totalResCase + (collection.rescase || 0),
                totalCollection: acc.totalCollection + (collection.totalCollection || 0),
                totalNorm: acc.totalNorm + (collection.norm || 0),
                totalRbPercentage: acc.totalRbPercentage + (collection.rbPercentage || 0),
                rbCount: acc.rbCount + (collection.rbPercentage ? 1 : 0),
                count: acc.count + 1
            };
        }, {
            totalCases: 0,
            totalPos: 0,
            totalRes: 0,
            totalResCase: 0,
            totalCollection: 0,
            totalNorm: 0,
            totalRbPercentage: 0,
            rbCount: 0,
            count: 0
        });


        setStats(aggregatedStats);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const resetFilters = () => {
        let processType = '';
        if (activeTab === 'flow') {
            processType = 'Flow';
        } else if (activeTab === 'recovery') {
            processType = 'Recovery';
        }

        setFilters({
            supervisor: '',
            process: '',
            startDate: '',
            endDate: '',
            processType: processType
        });
        setPage(1);
    };

    const exportToCSV = () => {
        // Additional client-side filtering for export
        let exportData = collections;
        if (activeTab === 'flow') {
            exportData = collections.filter(item => item.process?.processType === 'Flow');
        } else if (activeTab === 'recovery') {
            exportData = collections.filter(item => item.process?.processType === 'Recovery');
        }

        const headers = ['Date', 'Supervisor', 'Process', 'Process Type', 'Case', 'POS', 'RES', 'RES Case', 'Total Collection', 'Current Provisional Billing', 'Final Provisional Billing'];

        const rows = exportData.map(c => [
            new Date(c.date).toLocaleDateString(),
            c.supervisor?.name || 'Unknown',
            c.process?.process || 'Unknown',
            c.process?.processType || 'Unknown',
            c.case,
            c.pos,
            c.res,
            c.rescase,
            c.totalCollection,
            c.currentProvisionalBilling || 0,
            c.finalProvisionalBilling || 0
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `collections_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);

        let processType = '';
        if (tab === 'flow') {
            processType = 'Flow';
        } else if (tab === 'recovery') {
            processType = 'Recovery';
        }

        setFilters({
            supervisor: '',
            process: '',
            startDate: '',
            endDate: '',
            processType: processType
        });

        // Reset Redux state for the previous tab
        dispatch({ type: GET_ALL_DAILY_COLLECTIONS_RESET });
        dispatch({ type: GET_DAILY_RECOVERY_COLLECTIONS_RESET });
        dispatch({ type: GET_DAILY_FLOW_COLLECTIONS_RESET });
    };

    // Filter collections for display (client-side filtering for extra safety)
    const getDisplayCollections = () => {
        let displayCollections = collections;
        if (activeTab === 'flow') {
            displayCollections = collections.filter(item => item.process?.processType === 'Flow');
        } else if (activeTab === 'recovery') {
            displayCollections = collections.filter(item => item.process?.processType === 'Recovery');
        }
        return displayCollections;
    };

    const displayCollections = getDisplayCollections();

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Error Messages */}
                {error && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FileText className="mr-2" size={24} />
                            Daily MIS Report
                        </h1>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="bg-blue-100 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-200 flex items-center"
                            >
                                <Filter size={18} className="mr-2" />
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </button>

                            <button
                                onClick={printTable}
                                disabled={displayCollections.length === 0}
                                className="bg-purple-100 text-purple-600 px-4 py-2 rounded-lg hover:bg-purple-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Printer size={18} className="mr-2" />
                                Print
                            </button>

                            <button
                                onClick={exportToCSV}
                                disabled={displayCollections.length === 0}
                                className="bg-green-100 text-green-600 px-4 py-2 rounded-lg hover:bg-green-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Download size={18} className="mr-2" />
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200">
                        <button
                            className={`px-6 py-3 text-sm font-medium ${activeTab === 'all'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => handleTabChange('all')}
                        >
                            All Collections
                        </button>
                        <button
                            className={`px-6 py-3 text-sm font-medium ${activeTab === 'flow'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => handleTabChange('flow')}
                        >
                            Flow
                        </button>
                        <button
                            className={`px-6 py-3 text-sm font-medium ${activeTab === 'recovery'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                            onClick={() => handleTabChange('recovery')}
                        >
                            Recovery
                        </button>
                    </div>

                    {/* Filters */}
                    {showFilters && (
                        <div className="p-6 bg-gray-50 border-b border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Supervisor</label>
                                    <select
                                        name="supervisor"
                                        value={filters.supervisor}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Supervisors</option>
                                        {supervisors.map(s => (
                                            <option key={s._id} value={s._id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Process</label>
                                    <select
                                        name="process"
                                        value={filters.process}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Processes</option>
                                        {processes.map(p => (
                                            <option key={p._id} value={p._id}>
                                                {p.process} ({p.processType})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={resetFilters}
                                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                                >
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Statistics */}
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <BarChart className="mr-2" size={20} />
                            {activeTab === 'all' ? 'Summary Statistics' :
                                activeTab === 'flow' ? 'Flow Process Statistics' : 'Recovery Process Statistics'}
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="text-sm text-blue-500">Total Cases</p>
                                <p className="text-2xl font-bold text-blue-700">{stats.totalCases || 0}</p>
                            </div>

                            <div className="bg-green-50 p-4 rounded-lg">
                                <p className="text-sm text-green-500">Total POS</p>
                                <p className="text-2xl font-bold text-green-700">{stats.totalPos || 0}</p>
                            </div>

                            <div className="bg-purple-100 p-4 rounded-lg">
                                <p className="text-sm text-purple-500">Total RES%</p>
                                <p className="text-2xl font-bold text-purple-700">{stats.totalRes || 0}</p>
                            </div>

                            <div className="bg-yellow-100 p-4 rounded-lg">
                                <p className="text-sm text-yellow-600">Total RES Case</p>
                                <p className="text-2xl font-bold text-yellow-700">{stats.totalResCase || 0}</p>
                            </div>

                            <div className="bg-indigo-50 p-4 rounded-lg">
                                <p className="text-sm text-indigo-500">Total NORM</p>
                                <p className="text-2xl font-bold text-indigo-700">{stats.totalNorm || 0}</p>
                            </div>

                            <div className="bg-pink-50 p-4 rounded-lg">
                                <p className="text-sm text-pink-500">Total RB %</p>
                                <p className="text-2xl font-bold text-pink-700">{(stats.totalRbPercentage || 0).toFixed(2)}%</p>
                            </div>

                            <div className="bg-red-50 p-4 rounded-lg">
                                <p className="text-sm text-red-500">Total Collection</p>
                                <p className="text-2xl font-bold text-red-700">
                                    ₹{(stats.totalCollection || 0).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="overflow-x-auto w-full scrollbar-thin border rounded-lg">
                        {loading ? (
                            <div className="p-6 text-center">
                                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                                <p className="mt-2 text-gray-600">Loading data...</p>
                            </div>
                        ) : displayCollections.length > 0 ? (
                            <table className="min-w-[1100px] table-fixed divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="w-[120px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="w-[160px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supervisor</th>
                                        <th className="w-[140px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Process</th>
                                        <th className="w-[110px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="w-[80px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case</th>
                                        <th className="w-[80px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">POS</th>
                                        <th className="w-[80px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RES%</th>
                                        <th className="w-[100px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RES Case</th>
                                        <th className="w-[80px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">NORM</th>
                                        <th className="w-[80px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RB %</th>
                                        <th className="w-[120px] px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {displayCollections.map((collection) => (
                                        <tr key={collection._id} className="hover:bg-gray-50">
                                            <td className="w-[120px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{formatDate(collection.date)}</td>
                                            <td className="w-[160px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                {collection.supervisor?.name || 'Unknown'}
                                                <div className="text-xs text-gray-500">{collection.supervisor?.email}</div>
                                            </td>
                                            <td className="w-[140px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                    {collection.process?.process || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="w-[110px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 rounded-full text-xs ${collection.process?.processType === 'Flow'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : 'bg-green-100 text-green-800'
                                                        }`}
                                                >
                                                    {collection.process?.processType || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="w-[80px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{collection.case || 0}</td>
                                            <td className="w-[80px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{collection.pos || 0}</td>
                                            <td className="w-[80px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{collection.res || 0}</td>
                                            <td className="w-[100px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{collection.rescase || 0}</td>
                                            <td className="w-[80px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{collection.norm || 0}</td>
                                            <td className="w-[80px] px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{collection.rbPercentage || 0}%</td>
                                            <td className="w-[120px] px-6 py-4 text-sm font-medium text-blue-600 whitespace-nowrap">
                                                ₹{(collection.totalCollection || 0).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="p-6 text-center text-gray-500">
                                No collection data found matching your criteria.
                            </div>
                        )}

                        {/* Pagination */}
                        {displayCollections.length > 0 && (
                            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing page <span className="font-medium">{page}</span> of{' '}
                                        <span className="font-medium">{totalPages}</span>
                                    </p>
                                </div>

                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setPage(p => Math.max(p - 1, 1))}
                                        disabled={page === 1}
                                        className={`px-4 py-2 rounded ${page === 1 ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        Previous
                                    </button>

                                    <button
                                        onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                                        disabled={page === totalPages}
                                        className={`px-4 py-2 rounded ${page === totalPages ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DailyMIS;